import { DocumentView } from '@/components/views/DocumentView';
import { ChatView } from '@/components/views/ChatView';
import { VoiceChannelView } from '@/components/views/VoiceChannelView';
import { SpreadsheetView } from '@/components/views/SpreadsheetView';
import { PresentationView } from '@/components/views/PresentationView';
import { useAppStore } from '@/store/appStore';
import { FileQuestion } from 'lucide-react';

function ItemLoadingFallback() {
  return (
    <div className="flex-1 flex items-center justify-center bg-surface-primary">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-text-secondary text-sm">Caricamento...</span>
      </div>
    </div>
  );
}

/**
 * Caricamento finito e l'elemento non c'è: cancellato, indirizzo sbagliato, o
 * appartiene a un'organizzazione di cui non si fa parte. Prima si restituiva
 * `null`, cioè una pagina bianca senza spiegazione.
 */
function ItemNotFound({ what }: { what: string }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-surface-primary p-8">
      <div className="flex flex-col items-center gap-2 text-center max-w-sm">
        <FileQuestion className="w-8 h-8 text-text-quaternary" />
        <p className="text-text-secondary text-sm">{what} non trovato.</p>
        <p className="text-text-quaternary text-xs">
          Può essere stato eliminato, oppure appartiene a un'organizzazione
          diversa da quella attiva.
        </p>
      </div>
    </div>
  );
}

export function DocumentRoute() {
  const currentDocument = useAppStore((state) => state.currentDocument);
  const isLoadingItem = useAppStore((state) => state.isLoadingItem);
  const closeCurrentItem = useAppStore((state) => state.closeCurrentItem);

  if (isLoadingItem) {
    return <ItemLoadingFallback />;
  }

  if (!currentDocument) {
    return <ItemNotFound what="Documento" />;
  }

  return (
    <DocumentView
      document={currentDocument}
      onClose={closeCurrentItem}
    />
  );
}

export function ChatRoute() {
  const currentChat = useAppStore((state) => state.currentChat);
  const isLoadingItem = useAppStore((state) => state.isLoadingItem);
  const closeCurrentItem = useAppStore((state) => state.closeCurrentItem);

  if (isLoadingItem) {
    return <ItemLoadingFallback />;
  }

  if (!currentChat) {
    return <ItemNotFound what="Chat" />;
  }

  return (
    <ChatView
      chat={currentChat}
      onClose={closeCurrentItem}
    />
  );
}

export function VoiceChannelRoute() {
  const currentVoiceChannel = useAppStore((state) => state.currentVoiceChannel);
  const isLoadingItem = useAppStore((state) => state.isLoadingItem);
  const closeCurrentItem = useAppStore((state) => state.closeCurrentItem);

  if (isLoadingItem) {
    return <ItemLoadingFallback />;
  }

  if (!currentVoiceChannel) {
    return <ItemNotFound what="Canale vocale" />;
  }

  return (
    <VoiceChannelView
      channel={currentVoiceChannel}
      onClose={closeCurrentItem}
    />
  );
}

export function SpreadsheetRoute() {
  const currentSpreadsheet = useAppStore((state) => state.currentSpreadsheet);
  const isLoadingItem = useAppStore((state) => state.isLoadingItem);
  const closeCurrentItem = useAppStore((state) => state.closeCurrentItem);

  if (isLoadingItem) {
    return <ItemLoadingFallback />;
  }

  if (!currentSpreadsheet) {
    return <ItemNotFound what="Foglio di calcolo" />;
  }

  return (
    <SpreadsheetView
      spreadsheet={currentSpreadsheet}
      onClose={closeCurrentItem}
    />
  );
}

export function PresentationRoute() {
  const currentPresentation = useAppStore((state) => state.currentPresentation);
  const isLoadingItem = useAppStore((state) => state.isLoadingItem);
  const closeCurrentItem = useAppStore((state) => state.closeCurrentItem);

  if (isLoadingItem) {
    return <ItemLoadingFallback />;
  }

  if (!currentPresentation) {
    return <ItemNotFound what="Presentazione" />;
  }

  return (
    <PresentationView
      presentation={currentPresentation}
      onClose={closeCurrentItem}
    />
  );
}
